import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PARTNERS_ROUTE, WALLET_ROUTE } from '../../../routes/namedRoutes';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectChecklistAllStepsCompleted,
  selectChecklistCompletedSteps,
  selectMemberConfig, selectOnboardingChecklistViewCount,
  selectChecklistCompletedStateShown,
  UpdateOnboardingChecklistViewCount,
  UpdateChecklistCompletedStateShown,
  UpdateChecklistAllStepsCompleted,
} from '../../../store/slices/dashboardSlice';
import { useIsDesktop } from '../../../common/hooks/useResponsive';
import { DialogCTAComponent } from '../Partner/Enrollment/PartnerEnrollmentCardCommon';
import { TestIds } from '../../../assets/testIds';
import { useUpdateMemberConfigByKey } from '../../../common/useUpdateMemberConfigByKey';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { Logger } from '../../../config/logging/logger';
import { useUpdateChecklistCompletedStepsByKey } from './useUpdateChecklistCompletedStepsByKey';

export const useChecklistSteps = () => {
  const { t } = useTranslation();
  return [
    {
      key: t('checklist.steps.step1.key'),
      title: t('checklist.steps.step1.title'),
      description: t('checklist.steps.step1.description'),
      route: PARTNERS_ROUTE,
    },
    {
      key: t('checklist.steps.step2.key'),
      title: t('checklist.steps.step2.title'),
      description: t('checklist.steps.step2.description'),
      route: WALLET_ROUTE,
    },
    {
      key: t('checklist.steps.step3.key'),
      title: t('checklist.steps.step3.title'),
      description: t('checklist.steps.step3.description'),
      route: '',
    },
  ];
};

type ChecklistComponentProps = {
  scrollRef?: React.RefObject<HTMLDivElement>;
};

export function ChecklistComponent({ scrollRef }: ChecklistComponentProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  // === Constants for styles ===
  const gridContainerStyles = (bgColor: string) => ({
    p: theme.spacing(2),
    mt: theme.spacing(2),
    justifyContent: 'space-between',
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: bgColor,
  });

  const checkboxStyles = {
    p: theme.spacing(0),
    '&:hover': { backgroundColor: theme.palette.common.white },
    '& .MuiSvgIcon-root': {
      borderRadius: '50%',
      p: theme.spacing(0.25),
    },
  };

  const checklistWrapperStyles = {
    mb: theme.spacing(4),
    mt: theme.spacing(4),
    p: theme.spacing(1.5),
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    width: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '75%',
      backgroundColor: theme.palette.grey[50],
      p: theme.spacing(2.5),
    },
  };
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const completedSteps = useAppSelector(selectChecklistCompletedSteps);
  const allStepsCompleted = useAppSelector(selectChecklistAllStepsCompleted);
  const checklistViewCount = useAppSelector(selectOnboardingChecklistViewCount);
  const storeMemberConfig = useAppSelector(selectMemberConfig);
  const checklistCompletedStateShown = useAppSelector(selectChecklistCompletedStateShown);
  const checklistSteps = useChecklistSteps();
  const isDesktop = useIsDesktop();
  const updateMemberConfigByKey = useUpdateMemberConfigByKey();
  const updateChecklistStep = useUpdateChecklistCompletedStepsByKey();
  const { trackEngagement } = useCustomEventTracking();
  const maxOnboardingChecklistViewCount = 3;
  const logger = Logger.create('ChecklistComponent');
  const [showChecklist, setShowChecklist] = useState(false);

  // to control showing checklist based on
  // 1- member config,
  // 2- if all steps are completed and
  // 3- if completed state has already been shown
  useEffect(() => {
    if (storeMemberConfig) {
      setShowChecklist(storeMemberConfig.showOnboardingChecklist ||
        (allStepsCompleted && !checklistCompletedStateShown));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMemberConfig, checklistCompletedStateShown]);

  // to update completed state shown once
  useEffect(() => {
    if (!storeMemberConfig?.showOnboardingChecklist && allStepsCompleted) {
      dispatch(UpdateChecklistCompletedStateShown(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // to update member config when all steps are completed and send completed event
  useEffect(() => {
    if (storeMemberConfig?.showOnboardingChecklist &&
      completedSteps.length === checklistSteps.length) {
      updateMemberConfigByKey('showOnboardingChecklist', false, storeMemberConfig?.id as string);
      trackEngagement('OnboardingChecklist', 'Complete', 'AllStepsCompleted');
      dispatch(UpdateOnboardingChecklistViewCount(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedSteps]);

  const hideOnboardingChecklist = () => {
    setShowChecklist(false);
    dispatch(UpdateChecklistCompletedStateShown(true));
    trackEngagement('OnboardingChecklist', 'Dismiss', 'MaxNumberOfViewsReached');
    updateMemberConfigByKey('showOnboardingChecklist', false, storeMemberConfig?.id as string);
    // reset checklist view count in store
    dispatch(UpdateOnboardingChecklistViewCount(0));
  };

  useEffect(() => {
    logger.info(`Checking if onboarding checklist should be shown, view count: ${checklistViewCount}`);
    if (checklistViewCount < maxOnboardingChecklistViewCount) {
      dispatch(UpdateOnboardingChecklistViewCount(checklistViewCount + 1));
    } else {
      hideOnboardingChecklist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updateStepDone = (key: string) => {
    updateChecklistStep(key);
    if (!completedSteps.includes(key)) {
      trackEngagement('OnboardingChecklist', 'Click', key);
    }
    dispatch(UpdateOnboardingChecklistViewCount(0));
  };

  const handleClick = (key: string, route: string) => {
    updateStepDone(key);
    if (route) {
      navigate(route);
    } else if (scrollRef?.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClose = () => {
    dispatch(UpdateChecklistCompletedStateShown(true));
    dispatch(UpdateChecklistAllStepsCompleted(true));
    trackEngagement('OnboardingChecklist', 'Dismiss', 'UserClickedClose');
    if (storeMemberConfig?.id) {
      updateMemberConfigByKey('showOnboardingChecklist', false, storeMemberConfig.id);
    }
    dispatch(UpdateOnboardingChecklistViewCount(0));
  };

  if (!showChecklist) {
    return null;
  }

  return (
    <Box sx={checklistWrapperStyles}>
      <Box sx={{ position: 'relative' }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: { xs: theme.spacing(0), md: theme.spacing(0) },
            right: { xs: theme.spacing(0), md: theme.spacing(0) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {completedSteps.length === checklistSteps.length ? (
          <Typography variant="h2">
            {t('checklist.heading2')}
            <Typography variant="body1">
              {t('checklist.heading3')}
            </Typography>
          </Typography>
        ) : (
          <Typography variant="h2">{t('checklist.heading1')}</Typography>
        )}
      </Box>

      <Grid container direction="column">
        {checklistSteps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.key);
          const isLocked = index > completedSteps.length;
          const ctaLabel = isCompleted ? t('button.view') : t('button.start');
          const bgColor = isLocked ?
            theme.palette.grey[100] : theme.palette.common.white;

          return (
            <Grid
              data-testid={TestIds.checklist[`step${index + 1}` as keyof typeof TestIds.checklist]}
              key={step.key}
              container
              alignItems="center"
              onClick={!isLocked ? () => handleClick(step.key, step.route) :
                undefined}
              sx={{
                ...gridContainerStyles(bgColor),
                ...(isLocked ? {} : { cursor: 'pointer' }),
              }}
            >
              <Grid item xs={1} lg={0.5} alignSelf="start">
                <Checkbox
                  disabled={isLocked}
                  sx={checkboxStyles}
                  icon={
                    <RadioButtonUncheckedIcon
                      sx={{
                        color: isLocked ?
                          theme.palette.grey[600] :
                          theme.palette.common.black,
                      }}
                    />
                  }
                  checkedIcon={
                    <DoneIcon
                      fontSize="small"
                      sx={{
                        color: 'white',
                        backgroundColor: theme.palette.common.black,
                      }}
                    />
                  }
                  checked={isCompleted}
                />
              </Grid>

              <Grid item xs={10} md={8.5} lg={9}>
                <Typography variant="h15"
                  color={isLocked ? 'text.disabled' : theme.palette.common.black}
                >
                  {step.title}
                </Typography>
                <br />
                {!isCompleted && !isLocked && (
                  <Typography variant="body8">
                    {step.description}
                  </Typography>
                )}
              </Grid>

              <Grid item xs lg={1.5}>
                {isDesktop ? (
                  <Box sx={{
                    display: 'flex',
                    color: isLocked ? 'text.disabled' : theme.palette.common.black,
                  }}>
                    <Typography variant="h6">{ctaLabel}</Typography>
                    <ChevronRightIcon />
                  </Box>
                ) : (
                  <DialogCTAComponent
                    loading={false}
                    btnText={ctaLabel}
                    submitBtnTestId={TestIds.checklist.btn}
                    extraDisableCondition={isLocked}
                  />
                )}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
